import { useTranslation } from 'react-i18next';

import type { RoleSummary, UserScopeAssignment } from '@/shared/types/domain';
import { cn } from '@/shared/utils/cn';

import { inRoleOrder } from './roleOrder';

/**
 * A person's roles and where they work.
 *
 * My profile (PRF-02) lists them all, every role in the one order
 * (`roleOrder.ts`) and every place by name and kind: `RoleList`, `ScopeList`.
 *
 * OBS-007 — the account menu and the phone account sheet say them in two
 * lines that never grow (`AccessSummary`): the first role and the first place,
 * each with "+N" for the others, as the header's role line and onboarding's
 * owners count do. However many roles and places a person holds, the rows
 * under them keep their place, so Sign out is in view without scrolling. A
 * screen reader hears every name, never "+N"; a pointer's hover names the
 * others; My profile, the menu's first row, shows them all.
 *
 * PROVISIONAL: the two lines await Design (R-050-DEC-06) and the Product Owner
 * (docs/design-sync/OBS_007_ROLE_CONTEXT_DELTA_PROPOSED.md).
 *
 * Read from the session the shell already holds (`GET /me`): never a slug or
 * an internal scope key (PRF-17), and nothing here is a control. A role is
 * not something to pick: only the organisation's administrator changes roles
 * and where a person works (`account.profile.accessNote`).
 */
export function AccessSummary({
  roles,
  scopes,
}: {
  roles: RoleSummary[];
  scopes: UserScopeAssignment[];
}) {
  const { t } = useTranslation();
  const places = scopes.some((scope) => scope.type === 'tenant')
    ? [t('user.scopeTenant')]
    : scopes.flatMap((scope) => (scope.display_name ? [scope.display_name] : []));

  return (
    <div className="space-y-0.5">
      <SummaryLine
        label={t('account.profile.roles')}
        names={inRoleOrder(roles).map((role) => role.display_name)}
        none={t('account.profile.noRoles')}
      />
      <SummaryLine label={t('account.profile.scope')} names={places} none={t('app.none')} />
    </div>
  );
}

/** "Label: first +N" on one line, cut short rather than wrapped, so nothing under it moves. */
function SummaryLine({ label, names, none }: { label: string; names: string[]; none: string }) {
  return (
    <p className="type-meta flex min-w-0 gap-1 text-ink-muted">
      <span className="shrink-0">{label}:</span>{' '}
      {names.length > 0 ? (
        <FirstAndCount names={names} className="text-ink" />
      ) : (
        <span className="min-w-0 truncate">{none}</span>
      )}
    </p>
  );
}

/**
 * The first of `names`, then "+N" for the others (onboarding's StepOwner
 * count): what one line has room for. A screen reader hears every name,
 * never "+N"; a pointer's hover names the others.
 *
 * Its parent is the line: a flex row with a gap. The gap, not a margin, keeps
 * the count apart from the name in both directions — `.numeric` turns the
 * count's own direction to left to right in Arabic, which would turn a margin
 * on it to the outer side.
 */
export function FirstAndCount({ names, className }: { names: string[]; className?: string }) {
  const { t } = useTranslation();
  const first: string | undefined = names[0];
  const others = names.slice(1);
  const separator = t('amend.listSeparator');

  if (first === undefined) return null;

  return (
    <>
      <bdi className={cn('min-w-0 truncate', className)}>{first}</bdi>
      {others.length > 0 ? (
        <>
          <span aria-hidden="true" title={others.join(separator)} className="numeric shrink-0">
            +{others.length}
          </span>
          <span className="sr-only">{separator + others.join(separator)}</span>
        </>
      ) : null}
    </>
  );
}

/** Every role, one per line, in the one order; or that none is assigned. */
export function RoleList({ roles }: { roles: RoleSummary[] }) {
  const { t } = useTranslation();

  if (roles.length === 0) {
    return <span className="text-ink-muted">{t('account.profile.noRoles')}</span>;
  }

  return (
    <ul className="space-y-0.5">
      {inRoleOrder(roles).map((role) => (
        <li key={role.id}>
          <bdi>{role.display_name}</bdi>
        </li>
      ))}
    </ul>
  );
}

/** Where this person works, by name: the whole organisation, or its regions and units. */
export function ScopeList({ scopes }: { scopes: UserScopeAssignment[] }) {
  const { t } = useTranslation();

  if (scopes.some((scope) => scope.type === 'tenant')) return <>{t('user.scopeTenant')}</>;

  const named = scopes.filter((scope) => scope.display_name);

  if (named.length === 0) return <span className="text-ink-muted">{t('app.none')}</span>;

  return (
    <ul className="space-y-0.5">
      {named.map((scope) => (
        <li key={`${scope.type}:${scope.id ?? scope.display_name}`}>
          <span className="text-ink-muted">
            {scope.type === 'region' ? t('user.scopeRegion') : t('user.scopeBranch')}:
          </span>{' '}
          <bdi>{scope.display_name}</bdi>
        </li>
      ))}
    </ul>
  );
}
